import { GoogleMapApi } from 'app/src/map/utils'
import { useUserLanguage } from 'app/state'
import GoogleMapReact, { ChangeEventValue, Props as GoogleMapReactProps } from 'google-map-react'
import { isEmpty } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { useCallback, useRef } from 'react'
import { css, styled } from 'styled-components'
import { defaultCenter, defaultZoom, mapOptions } from '../../../map/utils/defaults'
import {
  useBackgroundMapContext,
  useBackgroundMapDispatcher
} from './contexts/BackgroundMapContext'

type ContainerProps = {
  $isHidden?: boolean
  $isEmbedded?: boolean
}

const Container = styled.div<ContainerProps>`
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: transparent;

  /** 
   * There's a child div of the map (that encloses Weenat children & GoogleMap helps) that gets on top & intercepts events. 
   * We drill through the tree to get to it and stop it from catching clicks & hover. 
   */
  & > div > div > div > .gm-style > div > div > div:not(.gmnoprint):not(.gm-style-cc) > div > div {
    pointer-events: none;
  }

  ${(p) =>
    p.$isHidden &&
    css`
      width: 0%;
      height: 0%;
      visibility: hidden;
    `}

  ${(p) =>
    p.$isEmbedded &&
    css`
      position: initial;
    `}
`

interface Props extends GoogleMapReactProps, ContainerProps {}

const BackgroundGoogleMap: FC<Props> = ({ $isHidden, $isEmbedded, children, ...props }) => {
  const [language] = useUserLanguage()
  const ref = useRef(null)

  const { markers, renderMarkers, onChange, drawMarkers } = useBackgroundMapContext()

  const dispatch = useBackgroundMapDispatcher()

  const bootstrapURLKeys = {
    key: import.meta.env.VITE_GOOGLE_API_KEY,
    libraries: ['places', 'geometry'],
    language
  }

  const initializeBackgroundMapApi = useCallback(
    (api: GoogleMapApi) => {
      dispatch({ type: 'setApi', newApi: api })
    },
    [dispatch]
  )

  const updateBackgroundMapDetails = useCallback(
    (event: ChangeEventValue) => {
      dispatch({
        type: 'setMapDetails',
        newMapDetails: {
          zoom: event.zoom,
          bbox: [event.bounds.nw.lng, event.bounds.se.lat, event.bounds.se.lng, event.bounds.nw.lat]
        }
      })

      if (!isNil(onChange)) onChange()
    },
    [dispatch, onChange]
  )

  return (
    <Container $isHidden={$isHidden} $isEmbedded={$isEmbedded}>
      <GoogleMapReact
        ref={ref}
        yesIWantToUseGoogleMapApiInternals
        bootstrapURLKeys={bootstrapURLKeys}
        options={mapOptions}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        onGoogleApiLoaded={initializeBackgroundMapApi}
        onChange={updateBackgroundMapDetails}
        debounced
        {...props}
      >
        {drawMarkers && !isEmpty(markers) && !isNil(renderMarkers)
          ? markers.map(renderMarkers)
          : null}
        {children}
      </GoogleMapReact>
    </Container>
  )
}

export default BackgroundGoogleMap
